import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  getFontFamilyLabel,
  getFontFamilyLabels,
  getFontFamilyValue,
} from "app/helpers/FontFamilies"
import { formatLabel } from "app/helpers/labels"
import { ButtonSelection } from "app/core/components/dashboard/d3/types"
import { getButtonType } from "app/core/components/dashboard/d3/button"

export const NewButtonDragDataTransferItemRect = "PlayerDash::NewButton::Rect"
export const NewButtonDragDataTransferItemEllipse = "PlayerDash::NewButton::Ellipse"
export const NewButtonDragDataTransferItemText = "PlayerDash::NewButton::Text"
export const NewButtonDragDataTransferItemImage = "PlayerDash::NewButton::Image"

export type UseStyleBarProps = {
  props: StyleBarProps
  reflectStyles: (state: Partial<StyleBarState>) => void
  selectTab: (tab: TabType) => void
}

export const useStyleBar = ({
  selectedButtons,
}: {
  selectedButtons: ButtonSelection | undefined
}): UseStyleBarProps => {
  const [styleBarState, setStyleBarState] = useState<StyleBarState>(initialStyleBarState)
  const [selectedTab, setSelectedTab] = useState<"object" | "text">("object")

  // for image buttons, only the "object" tab makes sense
  // reflecting style choices in the style bar is dealt with
  // in either the text editor overlay or the hidden text editors
  // (depending on whether one or multiple buttons are selected)
  useEffect(() => {
    if (selectedButtons && getButtonType(selectedButtons) === "image") {
      setSelectedTab("object")
    }
  }, [selectedButtons])

  return {
    props: {
      state: styleBarState,
      setStyleOption: (option, value) => {
        setStyleBarState({
          ...(styleBarState || initialStyleBarState),
          [option]: value,
        })
      },
      selectedTab,
      setSelectedTab,
    },
    reflectStyles: useCallback((newState: Partial<StyleBarState>) => {
      setStyleBarState((oldState) => ({ ...oldState, ...newState }))
    }, []),
    selectTab: setSelectedTab,
  }
}

export type StyleBarState = {
  enabledOptions?: ("text" | "object" | "backgroundColor" | "border" | "shadow")[]
  buttonType?: "rect" | "ellipse" | "image" | "text"
  borderStyle: "dotted" | "dashed" | "solid"
  borderColor: string
  borderWidth: number
  borderRadius: number
  backgroundColor: string
  color: string
  fontFamily: string
  textAlign: "left" | "right" | "center"
  verticalAlign: "top" | "middle" | "bottom"
  bold: boolean
  italics: boolean
  underline: boolean
  fontSize: number
  opacity: number
  shadowOffset: number
  shadowColor: string
  focus: boolean
}

export const TextOptions: (keyof StyleBarState)[] = [
  "color",
  "fontFamily",
  "textAlign",
  "bold",
  "italics",
  "underline",
  "fontSize",
]

export const ObjectOptions: (keyof StyleBarState)[] = [
  "borderStyle",
  "borderColor",
  "borderWidth",
  "borderRadius",
  "backgroundColor",
  "opacity",
  "shadowOffset",
  "shadowColor",
]

export const initialStyleBarState: StyleBarState = {
  buttonType: undefined,
  borderStyle: "solid",
  borderColor: "#000000",
  borderWidth: 1,
  borderRadius: 8,
  backgroundColor: "#ffffff",
  color: "#000000",
  fontFamily: "Helvetica, sans-serif",
  textAlign: "center",
  verticalAlign: "middle",
  bold: false,
  italics: false,
  underline: false,
  fontSize: 20,
  opacity: 100,
  shadowOffset: 0,
  shadowColor: "#000000",
  focus: false,
}

export type TabType = "object" | "text"

export type StyleBarProps = {
  userAppliedStyleOption?: <OptionType extends keyof StyleBarState>(
    option: OptionType,
    value: StyleBarState[OptionType],
    tab?: TabType,
  ) => Promise<void>
  state?: StyleBarState
  setStyleOption?: <OptionType extends keyof StyleBarState>(
    attribute: OptionType,
    value: StyleBarState[OptionType],
  ) => void
  selectedTab: TabType
  setSelectedTab: (tab: TabType) => void
  onSelectTab?: (tab: TabType) => void
  // additional fonts unlocked for the profile owning the dashboard
  enabledFonts?: string[]
}

const StyleBar = ({
  userAppliedStyleOption,
  onSelectTab,
  state,
  selectedTab,
  setSelectedTab,
  setStyleOption,
  enabledFonts,
}: StyleBarProps) => {
  const getUIState = useMemo(
    () => (attribute) => (state || initialStyleBarState)[attribute],
    [state],
  )
  const setUIState = useMemo(
    <OptionType extends keyof StyleBarState>() =>
      (attribute: OptionType, value: StyleBarState[OptionType], tab?: TabType) => {
        setStyleOption?.(attribute, value)
        ;(async () => userAppliedStyleOption?.(attribute, value, tab))()
      },
    [userAppliedStyleOption, setStyleOption],
  )

  // needed to bypass several bugs in Safari

  // we toggle this briefly to flash the color picker when there is an external value change
  // e.g. when the text editor selection is changed or a button is (de)selected
  const [showColorPicker, setShowColorPicker] = useState(true)
  // the value currently shown in the color picker
  const [currentTextColor, setCurrentTextColor] = useState<string | undefined>("#000001")

  const browserIsMacOSSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  }

  useEffect(() => {
    let newColor = state?.color ?? "#808080"
    if (browserIsMacOSSafari()) {
      // when the value of a "color" input is changed externally in Safari, the browser crashes
      // moreover, Safari registers no "change" nor "input" event when the user re-selects the current color
      // which causes problems when a range of text needs to be set to the color of the first letter
      // so in the case of Safari, we always show the same color
      newColor = "#000001"
    }
    if (newColor != currentTextColor && showColorPicker) {
      setShowColorPicker(false)
      setTimeout(() => {
        setCurrentTextColor(newColor)
        setShowColorPicker(true)
      }, 10)
    }
  }, [showColorPicker, currentTextColor, state?.color])

  return (
    <div>
      <div className="m-4">
        <h4>Buttons</h4>
        <div className="d-flex align-items-center justify-content-center" style={{ gap: "1.5em" }}>
          <button
            className="btn btn-link p-0"
            onClick={() => setUIState("buttonType", "rect")}
            draggable="true"
            onDragStart={(event) =>
              // TODO: show better preview image for drag'n'drop of buttons into dashboard
              // const previewImage = document.createElement("div")
              // previewImage.setAttribute("id", "dashboard_drag_preview")
              // previewImage.style.backgroundColor = "white"
              // previewImage.style.width = "150px"
              // previewImage.style.height = "100px"
              // previewImage.style.border = "1px solid black"
              // previewImage.style.borderRadius = "6px"
              // document.body.appendChild(previewImage)
              // event.dataTransfer.setDragImage(previewImage, 0, 0)
              event.dataTransfer.setData("text", NewButtonDragDataTransferItemRect)
            }
            // onDragEnd={(event) => {
            //   const previewImage = document.getElementById("dashboard_drag_preview")
            //   previewImage?.remove()
            // }}
          >
            <i className="bi bi-square button-icon" />
          </button>
          <button
            className="btn btn-link p-0"
            onClick={() => setUIState("buttonType", "ellipse")}
            draggable="true"
            onDragStart={(event) =>
              event.dataTransfer.setData("text", NewButtonDragDataTransferItemEllipse)
            }
          >
            <i className="bi bi-circle button-icon" />
          </button>
          <button
            className="btn btn-link p-0 text-decoration-none"
            onClick={() => setUIState("buttonType", "text")}
            draggable="true"
            onDragStart={(event) => {
              event.dataTransfer.setData("text", NewButtonDragDataTransferItemText)
            }}
          >
            <b>TEXT</b>
          </button>
          <button
            className="btn btn-link p-0"
            onClick={() => setUIState("buttonType", "image")}
            draggable="true"
            onDragStart={(event) =>
              event.dataTransfer.setData("text", NewButtonDragDataTransferItemImage)
            }
          >
            <i className="bi bi-image button-icon" />
          </button>
        </div>
      </div>
      {state?.enabledOptions?.includes("object") && state?.enabledOptions?.includes("text") && (
        <div className="d-flex border-bottom border-top border-muted">
          <button
            className={
              "btn flex-basis-0 flex-grow-1 d-flex-center rounded-0 border-0 border-end border-muted" +
              (selectedTab === "object" ? " btn-primary" : "")
            }
            onClick={() => {
              setSelectedTab("object")
              onSelectTab?.("object")
            }}
          >
            <h4 className="m-0">Object style</h4>
          </button>
          <button
            className={
              "btn flex-basis-0 flex-grow-1 d-flex-center rounded-0 border-0" +
              (selectedTab === "text" ? " btn-primary" : "")
            }
            onClick={() => {
              setSelectedTab("text")
              onSelectTab?.("text")
            }}
          >
            <h4 className="m-0">Text style</h4>
          </button>
        </div>
      )}
      <div className="m-4">
        <div
          style={
            (state?.enabledOptions?.includes("object") ?? true) && selectedTab === "object"
              ? {}
              : { display: "none" }
          }
        >
          <div className="d-flex flex-column">
            <h5 className="mt-2">Fill</h5>
            <div className="d-flex align-items-center justify-content-between color-picker my-1">
              {state?.enabledOptions?.includes("backgroundColor") && (
                <>
                  <label
                    className={getUIState("backgroundColor") === undefined ? "opacity-50" : ""}
                    htmlFor="color-input-background"
                  >
                    Color
                  </label>
                  <input
                    type="color"
                    className="form-control form-control-color mw-100"
                    id="color-input-background"
                    title="Choose a background color"
                    value={getUIState("backgroundColor") ?? "#808080"}
                    onChange={(event) =>
                      setUIState("backgroundColor", event.target.value, "object")
                    }
                    disabled={!state?.enabledOptions?.includes("backgroundColor")}
                    draggable={false}
                  />
                </>
              )}
            </div>
            <div
              className={
                "d-flex flex-column my-1" +
                (getUIState("opacity") === undefined ? " opacity-50" : "")
              }
            >
              <div className="d-flex align-items-center justify-content-between">
                <label htmlFor="opacity-input">Opacity</label>
                <span>{formatLabel(getUIState("opacity"), "%")}</span>
              </div>
              <input
                type="range"
                id="opacity-input"
                title="Choose the button opacity"
                min="0"
                max="100"
                step="1"
                value={Math.round(getUIState("opacity"))}
                onChange={(event) => setUIState("opacity", Number(event.target.value), "object")}
                disabled={!state?.enabledOptions?.includes("object")}
              />
            </div>
            {state?.enabledOptions?.includes("border") && (
              <>
                <hr className="internal-divider" />
                <h5 className="mt-2">Border</h5>
                <div className="d-flex align-items-center justify-content-between color-picker my-1">
                  <label
                    className={getUIState("borderColor") === undefined ? " opacity-50" : ""}
                    htmlFor="color-input-line"
                  >
                    Color
                  </label>
                  <input
                    type="color"
                    className="form-control form-control-color mw-100"
                    id="color-input-line"
                    title="Choose a line color"
                    value={getUIState("borderColor") ?? "#808080"}
                    onChange={(event) => setUIState("borderColor", event.target.value, "object")}
                    disabled={!state?.enabledOptions?.includes("object")}
                    draggable={false}
                  />
                </div>
                <div className="d-flex align-items-center justify-content-between dropdown-picker my-1">
                  <label
                    className={getUIState("borderStyle") === undefined ? " opacity-50" : ""}
                    htmlFor="border-style-dropdown"
                  >
                    Type
                  </label>
                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-outline-secondary formButton dropdown-toggle w-100 d-flex align-items-center py-3"
                      type="button"
                      id="border-style-dropdown"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      disabled={!state?.enabledOptions?.includes("object")}
                    >
                      <span
                        className="flex-grow-1"
                        style={{ borderTop: getUIState("borderStyle") + " 2px black" }}
                      />
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="border-style-dropdown">
                      {["dotted", "dashed", "solid"].map(
                        (lineStyleOption: StyleBarState["borderStyle"]) => (
                          <li key={lineStyleOption}>
                            <button
                              tabIndex={0}
                              className="btn btn-outline-secondary dropdown-item py-3"
                              onClick={() => {
                                setUIState("borderStyle", lineStyleOption, "object")
                              }}
                              disabled={!state?.enabledOptions?.includes("object")}
                            >
                              <div style={{ borderTop: lineStyleOption + " 2px black" }} />
                            </button>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                </div>
                <div
                  className={
                    "d-flex flex-column my-1" +
                    (getUIState("borderWidth") === undefined ? " opacity-50" : "")
                  }
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <label htmlFor="line-width-input">Width</label>
                    <span>{formatLabel(getUIState("borderWidth"), "px")}</span>
                  </div>
                  <input
                    type="range"
                    id="line-width-input"
                    title="Choose a line width"
                    min="0"
                    max="48"
                    step="1"
                    value={Math.round(getUIState("borderWidth"))}
                    onChange={(event) =>
                      setUIState("borderWidth", Number(event.target.value), "object")
                    }
                  />
                </div>
                <div
                  className={
                    "d-flex flex-column my-1" +
                    (getUIState("borderRadius") === undefined ? " opacity-50" : "")
                  }
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <label htmlFor="border-radius-input">Radius</label>
                    <span>{formatLabel(getUIState("borderRadius"), "px")}</span>
                  </div>
                  <input
                    type="range"
                    id="border-radius-input"
                    title="Choose a border radius"
                    min="0"
                    max="48"
                    step="1"
                    value={Math.round(getUIState("borderRadius"))}
                    onChange={(event) =>
                      setUIState("borderRadius", Number(event.target.value), "object")
                    }
                  />
                </div>
              </>
            )}
            {state?.enabledOptions?.includes("shadow") && (
              <>
                <hr className="internal-divider" />
                <h5 className="mt-2">Shadow</h5>
                <div className="d-flex align-items-center color-picker my-1">
                  <label
                    htmlFor="shadow-color-input"
                    className={
                      "form-label m-0" +
                      (getUIState("shadowColor") === undefined ? " opacity-50" : "")
                    }
                  >
                    Color
                  </label>
                  <input
                    type="color"
                    className="form-control form-control-color mw-100"
                    id="shadow-color-input"
                    title="Choose a shadow color"
                    value={getUIState("shadowColor")}
                    onChange={(event) => setUIState("shadowColor", event.target.value, "object")}
                    disabled={!state?.enabledOptions?.includes("shadow")}
                    draggable={false}
                  />
                </div>
                <div
                  className={
                    "d-flex flex-column my-1" +
                    (getUIState("shadowOffset") === undefined ? " opacity-50" : "")
                  }
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <label htmlFor="opacity-input">Offset</label>
                    <span>{formatLabel(getUIState("shadowOffset"), "px")}</span>
                  </div>
                  <input
                    type="range"
                    id="shadow-offset-input"
                    title="Choose the shadow offset"
                    min="0"
                    max="20"
                    step="1"
                    value={Math.round(getUIState("shadowOffset"))}
                    onChange={(event) =>
                      setUIState("shadowOffset", Number(event.target.value), "object")
                    }
                    disabled={!state?.enabledOptions?.includes("shadow")}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="m-4">
        <div
          style={
            (state?.enabledOptions?.includes("text") ?? true) && selectedTab === "text"
              ? {}
              : { display: "none" }
          }
        >
          <div className="d-flex flex-column">
            <h5 className="mt-2">Font</h5>
            <div
              className={
                "d-flex flex-column my-1" +
                (getUIState("fontSize") === undefined ? " opacity-50" : "")
              }
            >
              <div className="d-flex align-items-center justify-content-between">
                <label htmlFor="font-size-input">Size</label>
                <span>{formatLabel(getUIState("fontSize"), "pt")}</span>
              </div>
              <input
                type="range"
                id="font-size-input"
                title="Choose the font size"
                min="16"
                max="200"
                step="1"
                value={getUIState("fontSize") ?? 20}
                onMouseUp={() => setUIState("focus", true)}
                onTouchEnd={() => setUIState("focus", true)}
                onChange={(event) => setUIState("fontSize", Number(event.target.value), "text")}
                disabled={!state?.enabledOptions?.includes("text")}
              />
            </div>
            <div className="d-flex align-items-center dropdown-picker my-1">
              <label className={getUIState("fontFamily") === undefined ? "opacity-50" : ""}>
                Name
              </label>
              <div className="dropdown">
                <button
                  className="btn btn-sm btn-outline-secondary dropdown-toggle w-100 d-flex align-items-center"
                  type="button"
                  id="dropdownMenuButtonFont"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <span className="flex-grow-1 mx-1">
                    {getFontFamilyLabel(getUIState("fontFamily"))}
                  </span>
                </button>
                <ul className="dropdown-menu" aria-labelledby="dropdownMenuButtonFont">
                  {getFontFamilyLabels(enabledFonts).map((fontOption) => (
                    <li key={fontOption}>
                      <a
                        role="button"
                        tabIndex={0}
                        className="dropdown-item"
                        onClick={() => {
                          setUIState("fontFamily", getFontFamilyValue(fontOption), "text")
                        }}
                        onKeyDown={(event) => {
                          if (event.key === " " || event.key === "Enter") {
                            setUIState("fontFamily", getFontFamilyValue(fontOption), "text")
                          }
                        }}
                      >
                        <span>{fontOption}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between color-picker my-1">
              <label
                className={getUIState("color") === undefined ? " opacity-50" : ""}
                htmlFor="text-color-input"
              >
                Color
              </label>
              {showColorPicker && (
                <input
                  type="color"
                  className="form-control form-control-color mw-100"
                  id="text-color-input"
                  title="Choose a text color"
                  defaultValue={currentTextColor}
                  onInput={(event) => {
                    setCurrentTextColor((event.target as HTMLInputElement).value)
                    setUIState("color", (event.target as HTMLInputElement).value, "text")
                  }}
                  disabled={!state?.enabledOptions?.includes("text")}
                  draggable={false}
                />
              )}
              {/* show a placeholder color picker to prevent layout movement */}
              {!showColorPicker && (
                <input
                  type="color"
                  className="form-control form-control-color mw-100"
                  id="text-color-input"
                  title="Choose a text color"
                  defaultValue="#000000"
                  onInput={() => {}}
                  draggable={false}
                />
              )}
            </div>
            <hr className="internal-divider" />
            <h5 className="mt-2">Format</h5>
            <div className="d-flex flex-column" style={{ gap: "0.5em" }}>
              <div className="d-flex my-1" style={{ gap: "0.5em" }}>
                <button
                  className={
                    "btn px-2 formButton" +
                    (getUIState("bold") ? " btn-secondary active" : " btn-outline-secondary")
                  }
                  onClick={() => setUIState("bold", !getUIState("bold"), "text")}
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <i className="bi bi-type-bold" />
                </button>
                <button
                  className={
                    "btn px-2 formButton" +
                    (getUIState("italics") ? " btn-secondary active" : " btn-outline-secondary")
                  }
                  onClick={() => setUIState("italics", !getUIState("italics"), "text")}
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <i className="bi bi-type-italic" />
                </button>
                <button
                  className={
                    "btn px-2 formButton" +
                    (getUIState("underline") ? " btn-secondary active" : " btn-outline-secondary")
                  }
                  onClick={() => setUIState("underline", !getUIState("underline"), "text")}
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <i className="bi bi-type-underline" />
                </button>
              </div>
              <div className="d-flex my-1" style={{ gap: "0.5em" }}>
                <button
                  className={
                    "btn px-2 formButton" +
                    (getUIState("textAlign") === "left"
                      ? " btn-secondary active"
                      : " btn-outline-secondary")
                  }
                  onClick={() => setUIState("textAlign", "left", "text")}
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <i className="bi bi-text-left" />
                </button>
                <button
                  className={
                    "btn px-2 formButton" +
                    (getUIState("textAlign") === "center"
                      ? " btn-secondary active"
                      : " btn-outline-secondary")
                  }
                  onClick={() => setUIState("textAlign", "center", "text")}
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <i className="bi bi-text-center" />
                </button>
                <button
                  className={
                    "btn px-2 formButton" +
                    (getUIState("textAlign") === "right"
                      ? " btn-secondary active"
                      : " btn-outline-secondary")
                  }
                  onClick={() => setUIState("textAlign", "right", "text")}
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <i className="bi bi-text-right" />
                </button>
              </div>
              <div className="d-flex my-1" style={{ gap: "0.5em" }}>
                <button
                  className={
                    "btn px-2 formButton" +
                    (getUIState("verticalAlign") === "top"
                      ? " btn-secondary active"
                      : " btn-outline-secondary")
                  }
                  onClick={() => setUIState("verticalAlign", "top", "text")}
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <i className="bi bi-align-top" />
                </button>
                <button
                  className={
                    "btn px-2 formButton" +
                    (getUIState("verticalAlign") === "middle"
                      ? " btn-secondary active"
                      : " btn-outline-secondary")
                  }
                  onClick={() => setUIState("verticalAlign", "middle", "text")}
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <i className="bi bi-align-middle" />
                </button>
                <button
                  className={
                    "btn px-2 formButton" +
                    (getUIState("verticalAlign") === "bottom"
                      ? " btn-secondary active"
                      : " btn-outline-secondary")
                  }
                  onClick={() => setUIState("verticalAlign", "bottom", "text")}
                  disabled={!state?.enabledOptions?.includes("text")}
                >
                  <i className="bi bi-align-bottom" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <style>
          {`
          .form-control-color {
            width: 100px;
          }
          .formButton {
            border: 1px solid #ced4da;
            background: white;
          }
          h5, h6 {
            color: black;
          }
          .color-picker label {
            padding-right: 14px;
          }
          .color-picker > * {
            display: flex;
            flex: 1 0;
          }
          .dropdown-picker > * {
            display: flex;
            flex: 1 0;
          }
          .internal-divider {
            color: grey;
          }
          h4 {
            font-size: 1.5rem
          }
          .button-icon {
            font-size: 2.5rem;
          }
        `}
        </style>
      </div>
    </div>
  )
}

export default StyleBar
